import { FilterDefinition } from './FilterDefinition';

export class FilterDefinitionValidator {
  private static messagePrefix = 'Filter field validation failed:';

  /**
   * Validate an array of FilterDefinitions.
   * @throws Error if validation fails.
   */
  static validateFilterDefinitions(filterDefinitions: FilterDefinition[]) {
    this.validateLimitFields(filterDefinitions);
    this.validateCsvMetadataUniqueness(filterDefinitions);
    this.validateDatabaseMetadataUniqueness(filterDefinitions);
    this.validateLabelAndControlTypeConsistency(filterDefinitions);
    this.validateLimitFieldsHaveValidDataType(filterDefinitions);
    this.validateColumnOrderSequence(filterDefinitions);
  }

  private static validateLimitFields(filterDefinitions: FilterDefinition[]) {
    let limitFieldCount = 0;
    for (const fd of filterDefinitions) {
      if (fd.csvMetadata?.fieldType === 'limit') {
        limitFieldCount++;
        if (limitFieldCount > 1) {
          throw new Error(
            `${this.messagePrefix} more than one limit field is not allowed.`
          );
        }
      }
    }
  }

  private static validateCsvMetadataUniqueness(
    filterDefinitions: FilterDefinition[]
  ) {
    const csvColumnNames = new Map<string, boolean>();

    for (const fd of filterDefinitions) {
      if (!fd.csvMetadata) continue;
      const { columnName, fieldType, columnOrder } = fd.csvMetadata;
      const isDatabaseField = fieldType === 'database';
      const hasControl = fd.controlType !== undefined && isDatabaseField;

      if (csvColumnNames.has(columnName)) {
        if (hasControl && csvColumnNames.get(columnName)) {
          throw new Error(
            `${this.messagePrefix} more than one field with csvMetadata.columnName: '${columnName}' of type 'database' has a controlType.`
          );
        }
      } else {
        csvColumnNames.set(columnName, hasControl);
      }

      // Validate uniqueness of columnOrder (non-zero)
      if (
        columnOrder !== 0 &&
        filterDefinitions.filter(
          f => f.csvMetadata?.columnOrder === columnOrder
        ).length > 1
      ) {
        throw new Error(
          `${this.messagePrefix} duplicate columnOrder: '${columnOrder}' found.`
        );
      }
    }
  }

  private static validateDatabaseMetadataUniqueness(
    filterDefinitions: FilterDefinition[]
  ) {
    const databaseColumnNames = new Map<string, boolean>();

    for (const fd of filterDefinitions) {
      const databaseColumnName = fd.databaseMetadata?.columnName;
      if (!databaseColumnName) continue;

      const isDatabaseField = fd.csvMetadata?.fieldType === 'database';
      const hasControl = fd.controlType !== undefined && isDatabaseField;

      if (databaseColumnNames.has(databaseColumnName)) {
        if (hasControl && databaseColumnNames.get(databaseColumnName)) {
          throw new Error(
            `${this.messagePrefix} more than one field with databaseMetadata.columnName: '${databaseColumnName}' of type 'database' has a controlType.`
          );
        }
      } else {
        databaseColumnNames.set(databaseColumnName, hasControl);
      }
    }
  }

  private static validateLabelAndControlTypeConsistency(
    filterDefinitions: FilterDefinition[]
  ) {
    for (const fd of filterDefinitions) {
      const hasLabel = typeof fd.label === 'string';
      const hasControlType = fd.controlType !== undefined;
      const hasTooltip = typeof fd.toolTip === 'string';

      // If any of label/tooltip/controlType is present, ensure label & controlType are also present
      if (
        (hasLabel || hasControlType || hasTooltip) &&
        (!hasLabel || !hasControlType)
      ) {
        throw new Error(
          `${this.messagePrefix} if 'controlType' or 'toolTip' is provided, 'label' and 'controlType' must also be provided.`
        );
      }
    }
  }

  private static validateLimitFieldsHaveValidDataType(
    filterDefinitions: FilterDefinition[]
  ) {
    for (const fd of filterDefinitions) {
      if (fd.csvMetadata?.fieldType === 'limit') {
        const dataType = fd.databaseMetadata?.dataType;
        if (dataType !== 'number') {
          throw new Error(
            `${this.messagePrefix} field with label '${fd.label}' has 'limit' fieldType but does not have a 'number' dataType.`
          );
        }
      }
    }
  }

  private static validateColumnOrderSequence(
    filterDefinitions: FilterDefinition[]
  ) {
    const columnOrders = filterDefinitions
      .map(fd => fd.csvMetadata?.columnOrder)
      .filter(
        (order): order is number => typeof order === 'number' && order > 0
      );

    // Ensure all column orders form a complete sequence from 1 to max with no gaps
    const sortedColumnOrders = columnOrders.sort((a, b) => a - b);
    const maxColumnOrder =
      sortedColumnOrders[sortedColumnOrders.length - 1] || 0;
    const expectedOrders = Array.from(
      { length: maxColumnOrder },
      (_, i) => i + 1
    );
    const missingOrders = expectedOrders.filter(
      order => !sortedColumnOrders.includes(order)
    );

    if (missingOrders.length > 0) {
      throw new Error(
        `${this.messagePrefix} missing column orders: ${missingOrders.join(', ')}. Column orders should be sequential and complete, excluding those with order 0.`
      );
    }
  }
}
